import { CircleAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ModuleSubCategory, UserRole } from '../../apolloClient.types';
import Loader from '../../components/common/Loader';
import useDateFormats from '../../utils/useDateFormats';
import ModuleListView from './ModuleListView';
import { useFetchLaunchItemsQuery, useFetchTraineeAssessmentProgramsQuery } from './TraineeAssessment.api';
import useListReducer from './useListReducer';
import useAppAbilities from '@/components/abilities';
import Empty from '@/components/blocks/Empty';
import NotFound from '@/components/blocks/NotFound';
import Button from '@/components/common/Button';
import EmptyContainer from '@/components/common/EmptyContainer';
import PageHeader from '@/components/common/PageHeader';
import Select from '@/components/common/Select';
import { useGetCompanyId, useUser } from '@/contexts/UserSession';

const TraineeAssessmentPage = () => {
    const { assessmentRunId: defaultAssessmentRunId, subCategory: defaultSubCategory } = useParams<{
        assessmentRunId?: string;
        subCategory?: ModuleSubCategory;
    }>();
    const { t } = useTranslation('traineeAssessmentList');
    const abilities = useAppAbilities();
    const selectedCompanyId = useGetCompanyId();
    const user = useUser(true);
    const isTrainer = useMemo(() => user.__typename === 'TenantUser' && user.role === UserRole.Trainer, [user]);
    const { formatDateFromTo } = useDateFormats();

    const [assessmentRunId, setAssessmentRunId] = useState<string | null>(null);
    const [programId, setProgramId] = useState<string | null>(null);
    const [subCategory, setSubCategory] = useState<ModuleSubCategory>(
        // if user is trainer, set default subCategory to Psychological
        isTrainer ? ModuleSubCategory.Psychological : ModuleSubCategory.Cognitive
    );
    const [state, dispatch] = useListReducer();
    const { page, pageSize } = state;

    const { data, error, loading } = useFetchTraineeAssessmentProgramsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: selectedCompanyId || '',
            filter: { userIds: [user.id] },
        },
        skip: !selectedCompanyId || !user,
    });

    const programOptions = useMemo(() => {
        if (data?.programs?.items) {
            const programs = data.programs.items
                .filter(program => program.assessmentRuns && program.assessmentRuns.length > 0)
                .map(item => ({
                    value: item.id,
                    label: item.name,
                    moduleInstances:
                        item.courseInstance.executionSequence
                            .map(seq => seq?.moduleInstance)
                            .filter(module => module != null) || [],
                    assessmentRuns: item.assessmentRuns || [],
                    courseInstanceId: item.courseInstance.id,
                }));

            return programs.filter(p => p.moduleInstances.length && p.assessmentRuns.length);
        }

        return [];
    }, [data]);

    const assessmentRunOptions = useMemo(() => {
        return (
            data?.programs?.items
                ?.find(p => p.id === programId)
                ?.assessmentRuns?.map(assessment => ({
                    value: assessment.id,
                    label: `${assessment.name} (${formatDateFromTo(assessment.startDate, assessment.endDate)})`,
                    assessmentRun: assessment,
                })) || []
        );
    }, [data?.programs?.items, formatDateFromTo, programId]);

    useEffect(() => {
        const defaultProgram = programOptions.find(p => p.assessmentRuns?.some(ar => ar.id === defaultAssessmentRunId));
        if (defaultAssessmentRunId && defaultProgram) {
            setAssessmentRunId(defaultAssessmentRunId);
            setProgramId(defaultProgram.value);
        }
    }, [programOptions, defaultAssessmentRunId]);

    useEffect(() => {
        if (defaultSubCategory) {
            setSubCategory(defaultSubCategory);
        }
    }, [defaultSubCategory]);

    const onProgramChange = useCallback(
        (value: string | number | null) => {
            setProgramId(value?.toString() || null);
            setAssessmentRunId(null);
        },
        [setProgramId]
    );

    const onAssessmentRunChange = useCallback(
        (value: string | number | null) => {
            setAssessmentRunId(value?.toString() || null);
        },
        [setAssessmentRunId]
    );

    const onClearFilter = useCallback(() => {
        setAssessmentRunId(null);
        setProgramId(null);
    }, [setAssessmentRunId, setProgramId]);

    const program = useMemo(() => {
        return programOptions.find(p => p.value === programId) || null;
    }, [programId, programOptions]);

    const { data: launchData, loading: launchLoading } = useFetchLaunchItemsQuery({
        fetchPolicy: 'network-only',
        variables: {
            assessmentRunId: assessmentRunId || '',
            subCategory,
            pagination: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
            },
        },
        skip: !assessmentRunId,
    });

    // Reset pagination when assessmentRun or subCategory changes
    useEffect(() => {
        dispatch({ type: 'SET_PAGE', page: 1 });
    }, [assessmentRunId, subCategory, dispatch]);

    if (!abilities.canViewTraineeContentAssessment()) {
        return <NotFound />;
    }

    if (error) {
        return (
            <EmptyContainer>
                <Empty title={t('empty.title')} />
            </EmptyContainer>
        );
    }

    if (loading && !data?.programs?.items) {
        return <Loader />;
    }

    if (programOptions.length === 0) {
        return (
            <>
                <PageHeader title={t('title')} />
                <div className="mt-16 flex w-full justify-center rounded-xl bg-white lg:p-40">
                    <div className="max-w-lg">
                        <div className="text-center">
                            <CircleAlert className="mx-auto h-12 w-12 text-gray-500" strokeWidth={1.5} />
                            <div className="mt-5 text-base font-medium text-neutral-900">{t('warningTitle')}</div>
                            <p className="mt-1 text-base text-gray-500">{t('warningDescription')}</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="flex w-full flex-col gap-5 lg:gap-10">
            <PageHeader title={t('title')} />

            <div className="grid items-end gap-4 lg:grid-cols-[1fr_1fr_120px]">
                <div className="grid">
                    <Select
                        name="programme"
                        value={programId || null}
                        options={programOptions}
                        onChange={onProgramChange}
                        label={t('selectProgram')}
                        placeholder={t('selectProgramPlaceholder')}
                    />
                </div>
                <div className="grid">
                    <Select
                        name="assessmentRunId"
                        value={assessmentRunId || null}
                        options={assessmentRunOptions}
                        onChange={onAssessmentRunChange}
                        label={t('selectAssessmentRun')}
                        placeholder={t('selectAssessmentRunPlaceholder')}
                    />
                </div>
                <div className="justify-self-start lg:justify-self-end">
                    <Button onClick={onClearFilter} className="auto-cols-fr" outline>
                        {t('clearAll')}
                    </Button>
                </div>
            </div>

            <div className="col-span-2">
                <ModuleListView
                    assessmentRun={
                        assessmentRunOptions.find(item => item.value === assessmentRunId)?.assessmentRun || null
                    }
                    launchItems={launchData?.moduleInstancesForLaunch?.items || []}
                    totalCount={launchData?.moduleInstancesForLaunch?.count || 0}
                    courseInstanceId={program?.courseInstanceId || ''}
                    loading={launchLoading}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={p => dispatch({ type: 'SET_PAGE', page: p })}
                    onPageSizeChange={s => dispatch({ type: 'SET_PAGE_SIZE', pageSize: s })}
                    activeSubCategory={subCategory}
                    onSubCategoryChange={setSubCategory}
                />
            </div>
        </div>
    );
};
export default TraineeAssessmentPage;
